import React, { useState } from "react";
import Swal from "sweetalert2";
import { Button } from "../../Button";
import Form from "../../Form";
import { Input } from "../../Input";
import Modal from "../../Modal/Modal";
import Titulo from "../../Modal/ModaisConfigsPrimeiroAcesso/Titulo/Titulo";
import { Toggle } from "../../Toggle/Toggle";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { PedidosApi } from "../../../hooks/pedidosApi";
import ObservacaoPedido from "../../../types/ObservacaoPedido";
import { ObservacoesPedidoContainer, ModalContainer } from "./styles";
import moment from "moment";
import { Textarea } from "../../Textarea";

interface ObservacoesPedidoProps {
  idPedido: string;
  observacoes: ObservacaoPedido[];
  onObservacoesChange: (observacoes: ObservacaoPedido[]) => void;
}

const ObservacoesPedido: React.FC<ObservacoesPedidoProps> = ({
  idPedido,
  observacoes,
  onObservacoesChange,
}) => {
  const { theme } = useTheme();
  const pedidosApi = PedidosApi();

  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [editingObservacao, setEditingObservacao] = useState<ObservacaoPedido | null>(null);

  const [texto, setTexto] = useState("");
  const [visivelCliente, setVisivelCliente] = useState(false);

  const resetForm = () => {
    setTexto("");
    setVisivelCliente(false);
    setEditingObservacao(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (observacao: ObservacaoPedido) => {
    setEditingObservacao(observacao);
    setTexto(observacao.texto);
    setVisivelCliente(observacao.visivel_cliente);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!texto.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo obrigatório",
        text: "Digite uma observação antes de salvar.",
        confirmButtonText: "OK",
      });
      return;
    }

    setLoading(true);

    try {
      if (editingObservacao?.id) {
        // Atualizar observação existente
        const observacaoAtualizada = await pedidosApi.atualizarObservacaoPedido(
          idPedido,
          editingObservacao.id,
          { texto, visivel_cliente: visivelCliente }
        );

        const novasObservacoes = observacoes.map((obs) =>
          obs.id === editingObservacao.id ? observacaoAtualizada : obs
        );
        onObservacoesChange(novasObservacoes);

        Swal.fire({
          icon: "success",
          title: "Observação atualizada com sucesso!",
          showConfirmButton: false,
          timer: 1000,
        });
      } else {
        const novaObservacao = await pedidosApi.adicionarObservacaoPedido(
          idPedido,
          { texto, visivel_cliente: visivelCliente }
        );

        onObservacoesChange([...observacoes, novaObservacao]);

        Swal.fire({
          icon: "success",
          title: "Observação adicionada com sucesso!",
          showConfirmButton: false,
          timer: 1000,
        });
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: editingObservacao
          ? "Erro ao atualizar observação!"
          : "Erro ao adicionar observação!",
        showConfirmButton: false,
        timer: 1500,
      });
    }

    setLoading(false);
  };

  const handleRemover = async (observacao: ObservacaoPedido) => {
    if (!observacao.id) return;

    const result = await Swal.fire({
      title: "Tem certeza?",
      text: "Deseja remover esta observação?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      await pedidosApi.removerObservacaoPedido(idPedido, observacao.id);

      const novasObservacoes = observacoes.filter((obs) => obs.id !== observacao.id);
      onObservacoesChange(novasObservacoes);

      Swal.fire({
        icon: "success",
        title: "Observação removida com sucesso!",
        showConfirmButton: false,
        timer: 1000,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro ao remover observação!",
        showConfirmButton: false,
        timer: 1500,
      });
    }
  };

  return (
    <>
      <Modal
        isOpen={modalOpen}
        setModal={setModalOpen}
        width="98vw"
        maxWidth="100rem"
        onUseEffect={() => {}}
      >
        <ModalContainer theme={theme}>
          <Titulo
            titulo={editingObservacao ? "Editar observação" : "Nova observação do pedido"}
            subtitulo=""
            justifyContent="center"
          />
          <Form
            id="formObservacao"
            onSubmit={handleSubmit}
            setFormValid={setFormValid}
          >
            <div className="form-content">
              <Textarea
                maxLength={500}
                type="text"
                value={texto}
                onChange={(newValue) => setTexto(newValue)}
                placeholder="Digite a observação do pedido..."
              />

              <div className="toggle-container">
                <Toggle
                  value={visivelCliente}
                  onChange={(newValue) => setVisivelCliente(newValue)}
                />
                <span className="texto fonteTexto">Observação visível para o cliente?</span>
              </div>

              <div className="botoes">
                <div
                  className="cancelar"
                  onClick={() => {
                    setModalOpen(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </div>
                <Button
                  title={editingObservacao ? "Salvar" : "Adicionar"}
                  onClick={handleSubmit}
                  loading={loading}
                />
              </div>
            </div>
          </Form>
        </ModalContainer>
      </Modal>

      <ObservacoesPedidoContainer theme={theme}>
        <div className="header">
          <div className="subtitulo-pedido fonteSubtitulo" style={{fontWeight: 'bold'}}>Observações do pedido</div>
          <Button
            title="+ Nova observação"
            onClick={openAddModal}
            className="fonteTexto"
          />
        </div>

        {observacoes.length === 0 ? (
          <div className="sem-observacoes fonteRotulos">
            Nenhuma observação adicionada.
          </div>
        ) : (
          <div className="lista-observacoes">
            {observacoes.map((observacao, index) => (
              <div key={observacao.id || index} className="observacao-item">
                <div className="observacao-content">
                  <div className="observacao-texto fonteTexto">
                    {observacao.texto}
                  </div>
                  <div className="linha-menor"></div>
                  <div className="observacao-info">
                    {observacao.visivel_cliente ? (
                      <span className="info fonteRotulos">Visível para o cliente - </span>
                    ) : (
                      <span className="info fonteRotulos">Interna - </span>
                    )}
                    {observacao.data_criacao && (
                      <span className="data fonteRotulos">
                        {moment(observacao.data_criacao).format("DD/MM/YYYY HH:mm")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="observacao-acoes">
                  <span
                    className="editar fonteRotulos"
                    onClick={() => openEditModal(observacao)}
                  >
                    Editar
                  </span>
                  <span
                    className="remover fonteRotulos"
                    onClick={() => handleRemover(observacao)}
                  >
                    Remover
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </ObservacoesPedidoContainer>
    </>
  );
};

export default ObservacoesPedido;
